import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { GeoLocation, LocationType } from '@wilson/interfaces';
import { map, Observable, shareReplay } from 'rxjs';

interface GeoLocationJSON {
  id: string;
  lat: number;
  lc: string | null;
  lcat: string;
  lng: number;
  n: string;
}

/**
 * Provides access to bundled rail-station locations shipped with the mobile app
 * (`geolocations_minified.json`).
 *
 * The data is loaded once via HTTP from the app bundle and cached in memory.
 * Use `localLocationData$` for reactive access or `getById()` for synchronous lookups.
 */
@Injectable({
  providedIn: 'root',
})
export class LocalLocationStoreService {
  private httpClient = inject(HttpClient);
  private railLocationMap: Map<string, GeoLocation> | null = null;

  public readonly localLocationData$: Observable<Map<string, GeoLocation>> =
    this.httpClient
      .get<GeoLocationJSON[]>('/assets/data/geolocations_minified.json')
      .pipe(
        map((data) => {
          const locationMap = new Map<string, GeoLocation>();

          data.forEach((item) => {
            locationMap.set(item.id, {
              addressId: undefined,
              description: undefined,
              id: item.id,
              latitude: item.lat,
              locationCategory: {
                name: LocationType.Rail,
              },
              locationCategoryId: item.lcat,
              locationCode: item.lc,
              longitude: item.lng,
              name: item.n,
            } as GeoLocation);
          });
          return locationMap;
        }),
        shareReplay(1),
      );

  constructor() {
    this.localLocationData$.subscribe((map) => (this.railLocationMap = map));
  }

  getById(id: string): GeoLocation | null {
    return this.railLocationMap?.get(id) ?? null;
  }
}
